import { Linking } from "react-native";
import * as WebBrowser from "expo-web-browser";

import { LanguageType } from "@libs/i18n";
import { SUPPORT_PHONE_NUMBER_URL } from "@/domain/constants";
import { getLanguage } from "@/i18n/utils";

import { logger } from "./logger";

/**
 * Opens a URL in the external browser if it can be opened.
 * This is useful for links that should open in the user's default web browser.
 *
 * @param url - The URL to open.
 */
export async function openLinkInExternalBrowser(url: string): Promise<void> {
  try {
    const canOpen = await Linking.canOpenURL(url);
    if (canOpen) {
      await Linking.openURL(url);
    } else {
      logger.warn(`Can't open URL: ${url}`);
    }
  } catch (error) {
    logger.error({
      message: `Failed to open URL: ${url}`,
      error,
    });
  }
}

/**
 * Opens a URL in an in-app browser using Expo's WebBrowser module.
 * This is useful for links that should open within the app's context.
 *
 * On Android, it uses ChromeCustomTabs and on iOS, it uses SFSafariViewController.
 *
 * Note: This is different from rendering a web view within the app.
 *
 * @param url - The URL to open.
 */
export async function openLinkInInternalBrowser(url: string): Promise<void> {
  try {
    const canOpen = await Linking.canOpenURL(url);
    if (canOpen) {
      await WebBrowser.openBrowserAsync(url);
    } else {
      logger.warn(`Can't open URL: ${url}`);
    }
  } catch (error) {
    logger.error({
      message: `Failed to open URL: ${url}`,
      error,
    });
  }
}

const TERMS_URL_MAP: Record<LanguageType, string> = {
  en: "https://na2.hubs.ly/H040S4P0",
  es: "https://na2.hubs.ly/H040S280",
  ar: "https://na2.hubs.ly/H040S2J0",
  zh: "https://na2.hubs.ly/H040S5z0",
  vi: "https://na2.hubs.ly/H040Sgz0",
  ru: "https://na2.hubs.ly/H040Sgw0",
  tl: "https://na2.hubs.ly/H040Sgy0",
  ht: "https://na2.hubs.ly/H040Sgv0",
};

export const openTermsAndConditionsUrl = async (): Promise<void> => {
  const currentLanguage = getLanguage();
  const url = TERMS_URL_MAP[currentLanguage];
  await openLinkInExternalBrowser(url);
};

const PRIVACY_URL_MAP: Record<LanguageType, string> = {
  en: "https://na2.hubs.ly/H04yfT20",
  es: "https://na2.hubs.ly/H04ygSB0",
  ar: "https://na2.hubs.ly/H04yhm20",
  zh: "https://na2.hubs.ly/H04yjNS0",
  vi: "https://na2.hubs.ly/H04ykPJ0",
  ru: "https://na2.hubs.ly/H04ykcF0",
  tl: "https://na2.hubs.ly/H04ykk_0",
  ht: "https://na2.hubs.ly/H04ykkX0",
};

export const openPrivacyPolicyUrl = async (): Promise<void> => {
  const currentLanguage = getLanguage();
  const url = PRIVACY_URL_MAP[currentLanguage];
  await openLinkInExternalBrowser(url);
};

export const openSupportPhoneNumber = async (): Promise<void> => {
  try {
    await Linking.openURL(SUPPORT_PHONE_NUMBER_URL);
  } catch (error) {
    // Dismissing the system confirmation can reject, so a failure here is
    // usually a benign cancellation rather than a real error.
    logger.warn(`Support phone call was not opened: ${String(error)}`);
  }
};
